import { useAuthRolePickerOptions } from "@/lib/auth/users/users.api";
import Badge from "@/lib/components/ui/badge/badge";
import Command, {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/lib/components/ui/command/command";
import Check from "@/lib/components/ui/icons/check";
import ChevronDown from "@/lib/components/ui/icons/chevron-down";
import Search from "@/lib/components/ui/icons/search";
import X from "@/lib/components/ui/icons/x";
import Popover, {
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/ui/popover/popover";
import {
  selectTriggerVariants,
  type SelectTriggerVariants,
} from "@/lib/components/ui/select/select";
import { cn } from "@/lib/utils";
import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

type RolesSelectOption = {
  id: string;
  label: string;
  assignable: boolean;
};

type RolesSelectContextValue = {
  open: boolean;
  value: string[];
  options: RolesSelectOption[];
  disabled?: boolean;
  isLoading: boolean;
  removeValue: (id: string) => void;
  toggleValue: (id: string) => void;
  onOpenChange: (open: boolean) => void;
};

const RolesSelectContext = createContext<RolesSelectContextValue | null>(null);

function useRolesSelect(): RolesSelectContextValue {
  const ctx = useContext(RolesSelectContext);
  if (!ctx) {
    throw new Error("RolesSelect parts must be used within RolesSelect");
  }
  return ctx;
}

export type RolesSelectProps = {
  value: string[];
  enabled?: boolean;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  onValueChange: (value: string[]) => void;
  preserveRoleIds?: string[];
};

export function RolesSelect({
  value,
  enabled = true,
  children,
  disabled,
  className,
  onValueChange,
  preserveRoleIds,
}: RolesSelectProps) {
  const [open, setOpen] = useState(false);

  const rolesPicker = useAuthRolePickerOptions({
    enabled: enabled && !disabled,
    preserveRoleIds,
  });

  const options: RolesSelectOption[] = (rolesPicker.options ?? []).map(
    function toRoleOption(option) {
      return {
        id: option.role.id,
        label: option.role.name || option.role.key || option.role.id,
        assignable: option.assignable,
      };
    },
  );

  function toggleValue(id: string) {
    const option = options.find((item) => item.id === id);
    if (option && !option.assignable) {
      return;
    }
    if (value.includes(id)) {
      onValueChange(value.filter((selectedId) => selectedId !== id));
      return;
    }
    onValueChange([...value, id]);
  }

  function removeValue(id: string) {
    const option = options.find((item) => item.id === id);
    if (option && !option.assignable) {
      return;
    }
    onValueChange(value.filter((selectedId) => selectedId !== id));
  }

  const ctx: RolesSelectContextValue = {
    open,
    value,
    options,
    disabled,
    isLoading: rolesPicker.isLoading || rolesPicker.isFetching,
    removeValue,
    toggleValue,
    onOpenChange: setOpen,
  };

  return (
    <RolesSelectContext.Provider value={ctx}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className={cn("flex flex-col gap-2", className)}>{children}</div>
      </Popover>
    </RolesSelectContext.Provider>
  );
}

export type RolesSelectTriggerProps = ComponentProps<"button"> &
  Pick<SelectTriggerVariants, "size">;

export function RolesSelectTrigger({
  size = "md",
  children,
  className,
  ...props
}: RolesSelectTriggerProps) {
  const { t } = useTranslation();
  const { disabled, open } = useRolesSelect();

  return (
    <PopoverTrigger asChild>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          selectTriggerVariants({ size, width: "full" }),
          className,
        )}
        data-size={size}
        data-slot="roles-select-trigger"
        disabled={disabled}
        type="button"
        {...props}
      >
        <span className="text-foreground-pressed truncate">
          {children ?? t("settings_select_roles_placeholder")}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" />
      </button>
    </PopoverTrigger>
  );
}

export type RolesSelectContentProps = {
  className?: string;
};

export function RolesSelectContent({ className }: RolesSelectContentProps) {
  const { t } = useTranslation();
  const { isLoading, options, toggleValue, value } = useRolesSelect();

  return (
    <PopoverContent
      className={cn("w-(--radix-popover-trigger-width) p-0", className)}
      data-slot="roles-select-content"
    >
      <Command>
        <div
          className="flex items-center gap-2 border-b px-3"
          data-slot="command-input-wrapper"
        >
          <Search className="text-foreground-pressed size-4 shrink-0 opacity-50" />
          <CommandInput placeholder={t("settings_select_roles_search")} />
        </div>
        <CommandList>
          <CommandEmpty>
            {isLoading
              ? t("common_loading")
              : t("settings_select_roles_empty")}
          </CommandEmpty>
          <CommandGroup>
            {options.map(function renderRoleOption(option) {
              const selected = value.includes(option.id);
              return (
                <CommandItem
                  aria-selected={selected}
                  disabled={!option.assignable}
                  key={option.id}
                  keywords={[option.label]}
                  onSelect={function handleSelectRole() {
                    toggleValue(option.id);
                  }}
                  value={option.id}
                >
                  <Check
                    className={cn(
                      "shrink-0",
                      selected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );
}

export type RolesSelectValueProps = {
  className?: string;
};

export function RolesSelectValue({ className }: RolesSelectValueProps) {
  const { t } = useTranslation();
  const { disabled, options, removeValue, value } = useRolesSelect();

  if (value.length === 0) {
    return null;
  }

  const labelById = new Map(options.map((option) => [option.id, option.label]));
  const assignableById = new Map(
    options.map((option) => [option.id, option.assignable]),
  );

  return (
    <div
      className={cn("flex flex-wrap gap-1.5", className)}
      data-slot="roles-select-value"
    >
      {value.map(function renderRoleValue(id) {
        const label = labelById.get(id) ?? id;
        const assignable = assignableById.get(id) ?? true;
        return (
          <Badge key={id} radius="md" size="md" variant="secondary">
            <span className="max-w-48 truncate">{label}</span>
            {assignable ? (
              <button
                aria-label={t("settings_select_roles_remove", { label })}
                className="interactive -mr-0.5 inline-flex size-4 items-center justify-center rounded-sm opacity-70 hover:opacity-100"
                disabled={disabled}
                onClick={function handleRemoveRoleValue() {
                  removeValue(id);
                }}
                type="button"
              >
                <X className="size-3" />
              </button>
            ) : null}
          </Badge>
        );
      })}
    </div>
  );
}
